import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Book,
  FileText,
  Eye,
  Sparkles,
  Search,
  Download,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  X
} from 'lucide-react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, where, serverTimestamp, addDoc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getBytes } from 'firebase/storage';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../services/firestoreService';
import { isInternalUser } from '../services/accessControlService';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import SopRichEditor from '../components/sop/SopRichEditor';
import SopRichView from '../components/sop/SopRichView';
import { migrateSopRecord, flattenStepContent } from '../utils/sopContent';
import { fetchGroupEmojis, defaultGroupEmojis } from '../services/groupEmojiService';
import { CONTACT_TO_BUILD_STEPS, CONTACT_TO_BUILD_GOAL, CONTACT_TO_BUILD_NOTE } from '../data/contactToBuildDefault';
import ContactToBuildFlow from '../components/sop/ContactToBuildFlow';
import { fetchContactToBuildTemplate } from '../services/contactToBuildTemplateService';
import { fetchSopHtmlTemplate, renderSopHtmlTemplate, DEFAULT_SOP_HTML_TEMPLATE } from '../services/sopHtmlTemplateService';
import { downloadTailored } from '../utils/tailorDownloads';
import { sopJsonToHtml, isEmptyDoc } from '../components/sop/tiptap/sopJson';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PortalBackground from '../components/PortalBackground';

/** Styles for rendered SOP content (.sop-pdf) — shared by the PDF and DOC downloads. */
const buildSopContentStyles = (primaryColor) => `
        <style>
          .sop-pdf h2 { font-size: 20px; font-weight: bold; margin: 16px 0 8px; color: #111; }
          .sop-pdf h3 { font-size: 18px; font-weight: bold; margin: 0 0 8px; color: #111; }
          .sop-pdf p { margin: 6px 0; font-size: 14px; color: #000; line-height: 1.6; }
          .sop-pdf ul { list-style: disc; padding-left: 22px; margin: 6px 0; }
          .sop-pdf ul ul { list-style: circle; }
          .sop-pdf ul ul ul { list-style: square; }
          .sop-pdf ol { list-style: decimal; padding-left: 22px; margin: 6px 0; }
          .sop-pdf li { font-size: 14px; color: #000; margin: 4px 0; }
          .sop-pdf section[data-type="step-section"] { border: 1px solid #e5e7eb; border-left: 3px solid ${primaryColor}; border-radius: 8px; padding: 10px 14px; margin: 10px 0; page-break-inside: avoid; break-inside: avoid; }
          .sop-pdf [data-type="step-body"] { display: block !important; }
        </style>
      `;

// overrideCompanyId/overrideUserId let an internal viewer (CRM Portal View)
// load a specific client's SOPs instead of their own.
const SOPs = ({ embedded = false, overrideCompanyId = null, overrideUserId = null }) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const openParam = searchParams.get('open');
  // Guards the deep link so closing the modal doesn't immediately reopen it.
  const openedParamRef = useRef(null);
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSOP, setEditedSOP] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [sops, setSops] = useState({
    design: [],
    construction: [],
    office: []
  });
  const [userCustomizations, setUserCustomizations] = useState({});
  const modalRef = useRef(null);

  // Internal users (userType === 'internal') get full structural editing; portal
  // clients get the text-only lock. Client edits still write to their own
  // userSOPCustomizations doc — the master template is never touched.
  const [isInternal, setIsInternal] = useState(false);
  const [editedContentJSON, setEditedContentJSON] = useState(null);

  const [companyId, setCompanyId] = useState(null);
  const [contactToBuildSteps, setContactToBuildSteps] = useState([]);
  const [contactToBuildHasCustomization, setContactToBuildHasCustomization] = useState(false);
  const [contactToBuildEditing, setContactToBuildEditing] = useState(false);
  const [contactToBuildEditedSteps, setContactToBuildEditedSteps] = useState([]);
  const [contactToBuildModalOpen, setContactToBuildModalOpen] = useState(false);
  // Master template (CRM-managed) — the default steps/goal/note before any account customization.
  const [contactToBuildTemplateSteps, setContactToBuildTemplateSteps] = useState(CONTACT_TO_BUILD_STEPS);
  const [contactToBuildGoal, setContactToBuildGoal] = useState(CONTACT_TO_BUILD_GOAL);
  const [contactToBuildNote, setContactToBuildNote] = useState(CONTACT_TO_BUILD_NOTE);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [groupEmojis, setGroupEmojis] = useState(defaultGroupEmojis('sop'));
  // Footer container node for the editor's formatting toolbar (portaled in).
  const [toolbarSlot, setToolbarSlot] = useState(null);

  useEffect(() => {
    fetchGroupEmojis('sop').then(setGroupEmojis).catch(() => {});
  }, []);

  // Groups rendered as sales-materials-style sections; emoji is the section
  // fallback when an SOP template has no emoji of its own.
  const SOP_SECTIONS = [
    { id: 'design', label: 'Design', emoji: '📐' },
    { id: 'construction', label: 'Construction', emoji: '👷' },
    { id: 'office', label: 'Office', emoji: '🏢' },
  ];
  const CONTACT_TO_BUILD_SECTION = { id: 'contact-to-build', label: 'Contact to Build', emoji: '📋' };

  /** Firestore sometimes returns maps instead of arrays; templates use recursive { text, subItems }. */
  const normalizeSopList = (val) => {
    if (val == null) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'object') {
      const keys = Object.keys(val).filter((k) => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
      if (keys.length) return keys.map((k) => val[k]);
    }
    return [];
  };

  const getSopNodeText = (node) => {
    if (node == null) return '';
    if (typeof node === 'string') return node;
    if (typeof node === 'object') return node.text ?? node.label ?? '';
    return String(node);
  };

  const getStoragePathFromUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const isFirebaseStorageURL =
      url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
    if (!isFirebaseStorageURL) return null;
    const urlMatch = url.match(/\/o\/([^?]+)/);
    if (!urlMatch) return null;
    return decodeURIComponent(urlMatch[1]);
  };

  const getAttachmentMimeType = (fileName) => {
    const ext = (fileName || '').split('.').pop()?.toLowerCase();
    const mimeTypes = {
      pdf: 'application/pdf',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      csv: 'text/csv',
      txt: 'text/plain',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp'
    };
    return mimeTypes[ext] || 'application/octet-stream';
  };

  const resolveAttachmentBlob = async (attachment) => {
    if (!attachment?.url) throw new Error('Attachment URL missing');

    const storagePath = attachment.path || getStoragePathFromUrl(attachment.url);
    if (storagePath && currentUser) {
      const storageRef = ref(storage, storagePath);
      const bytes = await getBytes(storageRef);
      return new Blob([bytes], { type: getAttachmentMimeType(attachment.name) });
    }

    const response = await fetch(attachment.url);
    if (!response.ok) throw new Error('Failed to fetch attachment');
    return response.blob();
  };

  const handleAttachmentView = async (attachment) => {
    try {
      const blob = await resolveAttachmentBlob(attachment);
      const objectUrl = URL.createObjectURL(blob);
      const viewWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!viewWindow) {
        URL.revokeObjectURL(objectUrl);
        alert('Pop-up blocked. Please allow pop-ups to view this file.');
        return;
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (error) {
      console.error('Error viewing attachment:', error);
      alert('Error opening attachment. Please try again.');
    }
  };

  const handleAttachmentDownload = async (attachment) => {
    try {
      const blob = await resolveAttachmentBlob(attachment);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Error downloading attachment. Please try again.');
    }
  };


  useEffect(() => {
    if (overrideCompanyId) {
      setCompanyId(overrideCompanyId);
      return;
    }
    if (!currentUser?.uid) {
      setCompanyId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let cid = await getCompanyIdForUser(currentUser.uid);
        if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
        if (!cancelled) setCompanyId(cid || null);
      } catch {
        if (!cancelled) setCompanyId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?.email, overrideCompanyId]);

  const getCompanyIdForSave = async () => {
    if (overrideCompanyId) return overrideCompanyId;
    if (companyId) return companyId;
    if (!currentUser?.uid) return null;
    try {
      let cid = await getCompanyIdForUser(currentUser.uid);
      if (!cid && currentUser.email) cid = await findAccountIdByEmail(currentUser.email, 'Clients');
      if (cid) setCompanyId(cid);
      return cid || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchSOPs();
      fetchUserCustomizations();
      fetchContactToBuild();
    }
  }, [currentUser, companyId]);

  useEffect(() => {
    let cancelled = false;
    if (!currentUser?.uid) {
      setIsInternal(false);
      return undefined;
    }
    isInternalUser(currentUser.uid)
      .then((internal) => { if (!cancelled) setIsInternal(!!internal); })
      .catch(() => { if (!cancelled) setIsInternal(false); });
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const fetchContactToBuild = async () => {
    if (!currentUser) return;
    try {
      // The CRM-managed master template sets the default steps + goal + note.
      const template = await fetchContactToBuildTemplate();
      setContactToBuildTemplateSteps(template.steps);
      setContactToBuildGoal(template.goal);
      setContactToBuildNote(template.note);
      const templateSteps = () => template.steps.map((s) => ({ ...s }));

      if (!companyId) {
        setContactToBuildSteps(templateSteps());
        setContactToBuildHasCustomization(false);
        return;
      }
      const ctbDoc = await getDoc(doc(db, 'accounts', companyId, 'contactToBuildSOP', 'data'));
      if (ctbDoc.exists() && ctbDoc.data().steps?.length) {
        // Account has tailored its own copy — show that instead of the template.
        setContactToBuildSteps(ctbDoc.data().steps);
        setContactToBuildHasCustomization(true);
      } else {
        setContactToBuildSteps(templateSteps());
        setContactToBuildHasCustomization(false);
      }
    } catch (error) {
      console.error('Error fetching Contact to Build:', error);
      setContactToBuildSteps([...CONTACT_TO_BUILD_STEPS]);
      setContactToBuildHasCustomization(false);
    }
  };

  const fetchSOPs = async () => {
    if (!currentUser) return;
    try {
      const templateQuerySnapshot = await getDocs(collection(db, 'sopTemplates'));
      const templateSOPs = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      let accountSOPs = [];
      if (companyId) {
        try {
          const accountSOPsQuerySnapshot = await getDocs(collection(db, 'accounts', companyId, 'sops'));
          accountSOPs = accountSOPsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific SOPs:', error);
        }
      }

      // Combine template SOPs and account-specific SOPs
      const allSOPs = [...templateSOPs, ...accountSOPs];
      
      // Group SOPs by department, honoring the `order` set in the CRM template
      // editor. Sort is stable, so items without an order keep their sequence.
      const byOrder = (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      const groupedSOPs = {
        design: allSOPs.filter(sop => sop.department === 'design').sort(byOrder),
        construction: allSOPs.filter(sop => sop.department === 'construction').sort(byOrder),
        office: allSOPs.filter(sop => sop.department === 'office').sort(byOrder)
      };

      setSops(groupedSOPs);
    } catch (error) {
      console.error('Error fetching SOPs:', error);
    }
  };

  const fetchUserCustomizations = async () => {
    if (!currentUser) return;
    try {
      const collRef = collection(db, 'userSOPCustomizations');
      let scopeDocs = [];
      if (companyId) {
        const accountSnap = await getDocs(query(collRef, where('accountId', '==', companyId)));
        const byId = new Map();
        accountSnap.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
        const primaryUid = await getPrimaryUserIdForAccount(companyId);
        if (primaryUid) {
          const primarySnap = await getDocs(query(collRef, where('userId', '==', primaryUid)));
          primarySnap.docs.forEach(d => { if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() }); });
        }
        scopeDocs = Array.from(byId.values());
      } else {
        const snap = await getDocs(query(collRef, where('userId', '==', currentUser.uid)));
        scopeDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      const customizations = {};
      scopeDocs.forEach(d => {
        if (d.templateId) {
          customizations[d.templateId] = { id: d.id, ...d };
        }
      });
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching user customizations:', error);
    }
  };

  const handleSOPClick = useCallback((sop, event) => {
    // Prevent event propagation to avoid interference from other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate SOP data
    if (!sop || !sop.id) {
      console.error('Invalid SOP data:', sop);
      alert('Error: Invalid SOP data. Please try again.');
      return;
    }
    
    try {
      // Check if user has customizations
      const userCustomization = userCustomizations[sop.id];
      
      // Start with the template SOP data and ensure all required fields exist
      let displaySOP = {
        id: sop.id,
        title: sop.title || 'Untitled SOP',
        description: sop.description || '',
        department: sop.department || 'design',
        steps: sop.steps || [],
        ...sop
      };
      
      // If user has customizations, merge them with the template
      if (userCustomization) {
        displaySOP = {
          ...displaySOP,
          title: userCustomization.title || sop.title || 'Untitled SOP',
          description: userCustomization.description || sop.description || '',
          steps: userCustomization.steps || sop.steps || []
        };
      }

      // Content precedence: client customization JSON → template JSON → a live
      // conversion of the legacy fields (so un-migrated SOPs still render).
      // Flatten so any older step-format doc becomes the plain shape before it
      // reaches the editor (and so a save-without-edit persists plain).
      const effectiveJSON = flattenStepContent(
        (userCustomization && userCustomization.contentJSON) ||
          sop.contentJSON ||
          migrateSopRecord(displaySOP)
      );
      displaySOP.contentJSON = effectiveJSON;

      // Store the original template ID for saving customizations
      displaySOP.templateId = sop.id;
      displaySOP.hasCustomizations = !!userCustomization;

      setSelectedSOP(displaySOP);
      setEditedSOP(displaySOP);
      setEditedContentJSON(effectiveJSON);
      // View opens read-only; handleSOPTailor flips this to true right after.
      setIsEditing(false);
      setIsNewTemplate(false);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error handling SOP click:', error);
      alert('Error opening SOP. Please try again.');
    }
  }, [userCustomizations]);

  // Sidebar search deep link: /portal/sops?open=<docId|title> opens that SOP.
  useEffect(() => {
    if (!openParam || openedParamRef.current === openParam) return;
    const all = [...sops.design, ...sops.construction, ...sops.office];
    if (all.length === 0) return;
    const wanted = openParam.toLowerCase();
    const match =
      all.find((sop) => sop.id === openParam) ||
      all.find((sop) => (sop.title || '').toLowerCase() === wanted);
    if (!match) return;
    openedParamRef.current = openParam;
    handleSOPClick(match);
  }, [openParam, sops, handleSOPClick]);

  // Tailor opens the same popup but drops straight into edit mode.
  const handleSOPTailor = (sop, event) => {
    handleSOPClick(sop, event);
    setIsEditing(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const cid = await getCompanyIdForSave();
      const customizationData = {
        userId: overrideUserId || currentUser.uid,
        templateId: selectedSOP.templateId,
        title: editedSOP.title || '',
        description: editedSOP.description || '',
        steps: editedSOP.steps || [],
        department: editedSOP.department || '',
        // The client's edited Tiptap JSON lives on THEIR customization doc — this
        // overrides the template's contentJSON at read time; the master template
        // in sopTemplates is never written here.
        contentJSON: editedContentJSON || editedSOP.contentJSON || migrateSopRecord(editedSOP),
        updatedAt: serverTimestamp()
      };
      if (cid) customizationData.accountId = cid;

      if (selectedSOP.hasCustomizations) {
        const customizationId = userCustomizations[selectedSOP.templateId].id;
        const docRef = doc(db, 'userSOPCustomizations', customizationId);
        await updateDoc(docRef, customizationData);
      } else {
        const docRef = await addDoc(collection(db, 'userSOPCustomizations'), {
          ...customizationData,
          createdAt: serverTimestamp()
        });
        
        // Update local customizations state
        setUserCustomizations(prev => ({
          ...prev,
          [selectedSOP.templateId]: {
            id: docRef.id,
            ...customizationData
          }
        }));
      }
      
      // Update local state to show the customized version
      setSelectedSOP({
        ...editedSOP,
        hasCustomizations: true
      });
      
      // Update the SOPs display to show customized version
      setSops(prev => ({
        ...prev,
        [editedSOP.department]: prev[editedSOP.department].map(sop => 
          sop.id === selectedSOP.templateId ? {
            ...sop,
            title: editedSOP.title,
            description: editedSOP.description,
            steps: editedSOP.steps
          } : sop
        )
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving SOP customization:', error);
      alert('Error saving SOP customization. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedSOP({ ...selectedSOP });
    setIsEditing(false);
  };

  const handleResetToTemplate = async () => {
    try {
      if (selectedSOP.hasCustomizations) {
        // Delete the user customization
        const customizationId = userCustomizations[selectedSOP.templateId].id;
        await deleteDoc(doc(db, 'userSOPCustomizations', customizationId));
        
        // Remove from local state
        setUserCustomizations(prev => {
          const newCustomizations = { ...prev };
          delete newCustomizations[selectedSOP.templateId];
          return newCustomizations;
        });
        
        // Reset the SOP to template version
        const templateSOP = sops[selectedSOP.department].find(sop => sop.id === selectedSOP.templateId);
        const resetSOP = { ...templateSOP, templateId: templateSOP.id, hasCustomizations: false };
        
        setSelectedSOP(resetSOP);
        setEditedSOP(resetSOP);
        
        // Update SOPs display to show template version
        setSops(prev => ({
          ...prev,
          [selectedSOP.department]: prev[selectedSOP.department].map(sop => 
            sop.id === selectedSOP.templateId ? {
              ...templateSOP,
              title: templateSOP.title,
              description: templateSOP.description,
              steps: templateSOP.steps
            } : sop
          )
        }));
      }
    } catch (error) {
      console.error('Error resetting to template:', error);
    }
  };


  const handleContactToBuildEdit = () => {
    const stepsToEdit = contactToBuildSteps.length ? contactToBuildSteps : contactToBuildTemplateSteps;
    setContactToBuildEditedSteps(stepsToEdit.map(s => ({ ...s })));
    setContactToBuildEditing(true);
  };

  const handleContactToBuildSave = async () => {
    const cid = await getCompanyIdForSave();
    if (!cid) {
      alert('You must be linked to an account to save Contact to Build customizations.');
      return;
    }
    try {
      const docRef = doc(db, 'accounts', cid, 'contactToBuildSOP', 'data');
      await setDoc(docRef, {
        steps: contactToBuildEditedSteps,
        updatedAt: serverTimestamp()
      });
      setContactToBuildSteps(contactToBuildEditedSteps);
      setContactToBuildHasCustomization(true);
      setContactToBuildEditing(false);
    } catch (error) {
      console.error('Error saving Contact to Build:', error);
      alert('Error saving. Please try again.');
    }
  };

  const handleContactToBuildCancel = () => {
    setContactToBuildEditing(false);
  };

  const handleContactToBuildResetToTemplate = async () => {
    const cid = companyId || await getCompanyIdForSave();
    if (!cid) return;
    try {
      await deleteDoc(doc(db, 'accounts', cid, 'contactToBuildSOP', 'data'));
      setContactToBuildSteps(contactToBuildTemplateSteps.map((s) => ({ ...s })));
      setContactToBuildHasCustomization(false);
      setContactToBuildEditing(false);
    } catch (error) {
      console.error('Error resetting Contact to Build:', error);
    }
  };

  const handleContactToBuildStepChange = (index, field, value) => {
    const next = contactToBuildEditedSteps.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    setContactToBuildEditedSteps(next);
  };

  const handleContactToBuildAddRow = () => {
    setContactToBuildEditedSteps([...contactToBuildEditedSteps, { action: '', assignedTo: '' }]);
  };

  const handleContactToBuildDeleteRow = (index) => {
    setContactToBuildEditedSteps(contactToBuildEditedSteps.filter((_, i) => i !== index));
  };

  const handleContactToBuildMoveRow = (index, direction) => {
    const next = [...contactToBuildEditedSteps];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    setContactToBuildEditedSteps(next);
  };

  // Shared branding for PDFs: logo, company name, colors (used by Contact to Build and SOP modal PDFs)
  const getPdfBranding = async () => {
    let companyLogo = null;
    let companyName = '';
    let primaryColor = '#dc2626';
    let secondaryColor = '#1f2937';
    const brandingUid = overrideUserId || currentUser?.uid;
    if (brandingUid) {
      try {
        const userDoc = await getDoc(doc(db, 'users', brandingUid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.companyLogo) companyLogo = userData.companyLogo;
          if (userData.company) companyName = userData.company;
          if (userData.companyColors) {
            primaryColor = userData.companyColors.primary || primaryColor;
            secondaryColor = userData.companyColors.secondary || secondaryColor;
          }
        }
        if (!companyLogo && !overrideUserId) {
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            const userData = internalUserDoc.data();
            if (userData.companyLogo) companyLogo = userData.companyLogo;
            if (!companyName && userData.company) companyName = userData.company;
            if (userData.companyColors) {
              primaryColor = userData.companyColors.primary || primaryColor;
              secondaryColor = userData.companyColors.secondary || secondaryColor;
            }
          }
        }
      } catch (e) {
        console.error('Error fetching user settings:', e);
      }
    }
    const loadLogoAsDataUrl = async (url) => {
      try {
        const isFirebaseStorageURL = url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
        if (isFirebaseStorageURL && currentUser) {
          try {
            const urlMatch = url.match(/\/o\/([^?]+)/);
            if (urlMatch) {
              const storagePath = decodeURIComponent(urlMatch[1]);
              const storageRef = ref(storage, storagePath);
              const bytes = await getBytes(storageRef);
              let blobType = 'image/png';
              if (storagePath.endsWith('.svg')) blobType = 'image/svg+xml';
              else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) blobType = 'image/jpeg';
              else if (storagePath.endsWith('.gif')) blobType = 'image/gif';
              else if (storagePath.endsWith('.webp')) blobType = 'image/webp';
              const blob = new Blob([bytes], { type: blobType });
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const dataUrl = reader.result;
                  if (storagePath.endsWith('.svg') || blob.type === 'image/svg+xml') {
                    const img = new Image();
                    img.onload = () => {
                      try {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth || 200;
                        canvas.height = img.naturalHeight || 50;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                      } catch (err) {
                        resolve(dataUrl);
                      }
                    };
                    img.onerror = () => resolve(dataUrl);
                    img.src = dataUrl;
                  } else resolve(dataUrl);
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
            }
          } catch (err) {
            return null;
          }
        }
        if (url.startsWith('http') || url.startsWith('/')) {
          try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch');
            const blob = await response.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const dataUrl = reader.result;
                if (url.endsWith('.svg') || blob.type === 'image/svg+xml') {
                  const img = new Image();
                  img.onload = () => {
                    try {
                      const canvas = document.createElement('canvas');
                      canvas.width = img.naturalWidth || 200;
                      canvas.height = img.naturalHeight || 50;
                      const ctx = canvas.getContext('2d');
                      ctx.drawImage(img, 0, 0);
                      resolve(canvas.toDataURL('image/png'));
                    } catch (err) {
                      resolve(dataUrl);
                    }
                  };
                  img.onerror = () => resolve(dataUrl);
                  img.src = dataUrl;
                } else resolve(dataUrl);
              };
              reader.onerror = () => resolve(null);
              reader.readAsDataURL(blob);
            });
          } catch (err) {
            return null;
          }
        }
        return null;
      } catch (err) {
        return null;
      }
    };
    let logoDataUrl = null;
    let logoDimensions = { width: 200, height: 50 };
    if (companyLogo) {
      const isFirebaseStorageURL = companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
      const timeout = isFirebaseStorageURL ? 5000 : 2000;
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl(companyLogo).catch(() => null),
        new Promise((r) => setTimeout(() => r(null), timeout))
      ]);
    }
    if (!logoDataUrl) {
      logoDataUrl = await Promise.race([
        loadLogoAsDataUrl('/bba-logo.png').catch(() => null),
        new Promise((r) => setTimeout(() => r(null), 2000))
      ]);
    }
    if (logoDataUrl) {
      const img = new Image();
      await Promise.race([
        new Promise((resolve) => {
          img.onload = () => {
            logoDimensions = { width: img.naturalWidth || 200, height: img.naturalHeight || 50 };
            resolve();
          };
          img.onerror = () => resolve();
          img.src = logoDataUrl;
        }),
        new Promise((r) => setTimeout(r, 2000))
      ]);
    }
    return { logoDataUrl, logoDimensions, companyName, primaryColor, secondaryColor };
  };

  const handleDownloadContactToBuildPDF = async () => {
    const steps = contactToBuildEditing ? contactToBuildEditedSteps : (contactToBuildSteps.length ? contactToBuildSteps : contactToBuildTemplateSteps);
    try {
      const { logoDataUrl, logoDimensions, companyName } = await getPdfBranding();
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const pageMargin = 10;
      const logoHeight = logoDataUrl ? 20 : 0;
      const logoBottomMargin = logoDataUrl ? 5 : 0;
      const headerHeight = logoHeight + logoBottomMargin;
      const footerHeight = 15;
      const contentTop = pageMargin + headerHeight;
      const contentBottom = pageH - pageMargin - footerHeight;

      const addLogo = (pdfDoc) => {
        if (!logoDataUrl) return;
        try {
          const maxLogoHeight = 20;
          const maxLogoWidth = 70;
          const ratio = logoDimensions.width / logoDimensions.height;
          let h = maxLogoHeight;
          let w = h * ratio;
          if (w > maxLogoWidth) {
            w = maxLogoWidth;
            h = w / ratio;
          }
          const x = (pageW - w) / 2;
          pdfDoc.addImage(logoDataUrl, 'PNG', x, pageMargin, w, h);
        } catch (err) {
          console.warn('Error adding logo to PDF:', err);
        }
      };

      const addFooter = (pdfDoc, pageNum, totalPages) => {
        pdfDoc.setFontSize(10);
        pdfDoc.setTextColor(100, 100, 100);
        if (companyName) {
          pdfDoc.text(companyName, pageMargin, pageH - pageMargin);
        }
        const currentDate = new Date().toLocaleDateString();
        const dateWidth = pdfDoc.getTextWidth(currentDate);
        pdfDoc.text(currentDate, (pageW - dateWidth) / 2, pageH - pageMargin);
        const pageText = `Page ${pageNum} of ${totalPages}`;
        pdfDoc.text(pageText, pageW - pageMargin - pdfDoc.getTextWidth(pageText), pageH - pageMargin);
      };

      addLogo(pdf);
      let y = contentTop;
      const margin = 15;
      const col1W = 95;
      const col2X = margin + 100;

      pdf.setFontSize(18);
      pdf.text('Contact to Build SOP', pageW / 2, y, { align: 'center' });
      y += 10;
      pdf.setFontSize(11);
      const goalLines = pdf.splitTextToSize(contactToBuildGoal, pageW - 2 * margin);
      pdf.text(goalLines, margin, y);
      y += goalLines.length * 6 + 8;

      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Action', margin, y);
      pdf.text('Assigned to', col2X, y);
      y += 8;
      pdf.setFont(undefined, 'normal');
      pdf.setFontSize(10);

      const rowHeight = 7;
      for (let i = 0; i < steps.length; i++) {
        if (y > contentBottom) {
          pdf.addPage();
          addLogo(pdf);
          y = contentTop;
        }
        const action = (steps[i].action || '').substring(0, 55);
        const assigned = (steps[i].assignedTo || '').substring(0, 40);
        pdf.text(action, margin, y, { maxWidth: col1W });
        pdf.text(assigned, col2X, y, { maxWidth: pageW - col2X - margin });
        y += rowHeight;
      }

      y += 10;
      if (y > contentBottom - 20) {
        pdf.addPage();
        addLogo(pdf);
        y = contentTop;
      }
      pdf.setFont(undefined, 'italic');
      pdf.setFontSize(9);
      const noteLines = pdf.splitTextToSize(contactToBuildNote, pageW - 2 * margin);
      pdf.text(noteLines, margin, y);

      const totalPages = pdf.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        pdf.setPage(p);
        addFooter(pdf, p, totalPages);
      }
      pdf.save('contact_to_build_sop.pdf');
    } catch (error) {
      console.error('Error generating Contact to Build PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Contact to Build DOC download — same master HTML template pipeline as the
  // regular SOP DOC download, with the steps table rendered as HTML.
  const handleDownloadContactToBuildDoc = async () => {
    const steps = contactToBuildEditing ? contactToBuildEditedSteps : (contactToBuildSteps.length ? contactToBuildSteps : contactToBuildTemplateSteps);
    try {
      const [branding, master] = await Promise.all([getPdfBranding(), fetchSopHtmlTemplate()]);
      const { logoDataUrl, companyName, primaryColor, secondaryColor } = branding;

      const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const cellStyle = 'border:1px solid #d1d5db;padding:6px 10px;text-align:left;vertical-align:top;';
      const rows = steps
        .map((s) => `<tr><td style="${cellStyle}">${esc(s.action)}</td><td style="${cellStyle}">${esc(s.assignedTo)}</td></tr>`)
        .join('');
      const bodyHtml = `<div class="sop-pdf">
        <p>${esc(contactToBuildGoal)}</p>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr><th style="${cellStyle}font-weight:bold;">Action</th><th style="${cellStyle}font-weight:bold;">Assigned to</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <p><em>${esc(contactToBuildNote)}</em></p>
      </div>`;

      const templateHtml = (master.html || '').trim() || DEFAULT_SOP_HTML_TEMPLATE;
      const html = buildSopContentStyles(primaryColor) + renderSopHtmlTemplate(templateHtml, {
        logoUrl: logoDataUrl || '',
        title: 'Contact to Build SOP',
        body: bodyHtml,
        companyName,
        primaryColor,
        secondaryColor,
      });

      await downloadTailored('docx', { html, fileName: 'contact_to_build_sop' });
    } catch (error) {
      console.error('Error generating Contact to Build document:', error);
      alert('Error generating document. Please try again.');
    }
  };

  // Download the SOP as a Word document (.docx) rendered through the master
  // HTML template (CRM ▸ SOP Templates ▸ HTML Template tab). Falls back to the
  // built-in starter layout when no template is saved.
  const handleDownloadDoc = async () => {
    const sop = selectedSOP;
    if (!sop) return;
    try {
      const [branding, master] = await Promise.all([getPdfBranding(), fetchSopHtmlTemplate()]);
      const { logoDataUrl, companyName, primaryColor, secondaryColor } = branding;

      const json = flattenStepContent(sop.contentJSON || migrateSopRecord(sop));
      const bodyHtml = `<div class="sop-pdf">${sopJsonToHtml(json)}</div>`;

      const templateHtml = (master.html || '').trim() || DEFAULT_SOP_HTML_TEMPLATE;
      const html = buildSopContentStyles(primaryColor) + renderSopHtmlTemplate(templateHtml, {
        logoUrl: logoDataUrl || '',
        title: sop.title || '',
        body: bodyHtml,
        companyName,
        primaryColor,
        secondaryColor,
      });

      const fileName = `${String(sop.title || 'sop').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_sop`;
      await downloadTailored('docx', { html, fileName });
    } catch (error) {
      console.error('Error generating document:', error);
      alert('Error generating document. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    if (!modalRef.current) return;

    try {
      // Fetch user's company logo, colors, and company name
      let companyLogo = null;
      let companyName = '';
      let primaryColor = '#dc2626';
      let secondaryColor = '#1f2937';
      
      if (currentUser || overrideUserId) {
        try {
          // Try to get from users collection first (prioritize client profile logo)
          const userDoc = await getDoc(doc(db, 'users', overrideUserId || currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.companyLogo) {
              companyLogo = userData.companyLogo;
            }
            if (userData.company) {
              companyName = userData.company;
            }
            if (userData.companyColors) {
              primaryColor = userData.companyColors.primary || primaryColor;
              secondaryColor = userData.companyColors.secondary || secondaryColor;
            }
          }
          
          // Also check internal_users collection if companyLogo not found in users
          // This ensures we check both collections for the profile logo
          if (!companyLogo && !overrideUserId) {
            const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
            if (internalUserDoc.exists()) {
              const userData = internalUserDoc.data();
              if (userData.companyLogo) {
                companyLogo = userData.companyLogo;
              }
              // Only set company name and colors if not already set from users collection
              if (!companyName && userData.company) {
                companyName = userData.company;
              }
              if (userData.companyColors) {
                primaryColor = userData.companyColors.primary || primaryColor;
                secondaryColor = userData.companyColors.secondary || secondaryColor;
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user settings:', error);
        }
      }
      
      // Helper function to load logo as data URL, avoiding CORS issues
      const loadLogoAsDataUrl = async (url) => {
        try {
          // Check if it's a Firebase Storage URL
          const isFirebaseStorageURL = url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
          
          // For Firebase Storage URLs, ONLY use the SDK (never use fetch due to CORS)
          if (isFirebaseStorageURL && currentUser) {
            try {
              // Extract the storage path from the URL
              // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token=...
              const urlMatch = url.match(/\/o\/([^?]+)/);
              if (urlMatch) {
                const encodedPath = urlMatch[1];
                const storagePath = decodeURIComponent(encodedPath);
                
                console.log('Attempting to load company logo from Firebase Storage:', storagePath);
                
                // Use Firebase Storage SDK to download directly (bypasses CORS)
                const storageRef = ref(storage, storagePath);
                const bytes = await getBytes(storageRef);
                
                // Determine blob type from file extension or bytes
                let blobType = 'image/png'; // default
                if (storagePath.endsWith('.svg')) {
                  blobType = 'image/svg+xml';
                } else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) {
                  blobType = 'image/jpeg';
                } else if (storagePath.endsWith('.png')) {
                  blobType = 'image/png';
                } else if (storagePath.endsWith('.gif')) {
                  blobType = 'image/gif';
                } else if (storagePath.endsWith('.webp')) {
                  blobType = 'image/webp';
                }
                
                // Convert bytes to blob with proper type
                const blob = new Blob([bytes], { type: blobType });
                
                // Convert blob to data URL
                return new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const dataUrl = reader.result;
                    // If it's SVG, convert to PNG via canvas
                    if (storagePath.endsWith('.svg') || blob.type === 'image/svg+xml') {
                      const img = new Image();
                      img.onload = () => {
                        try {
                          const canvas = document.createElement('canvas');
                          canvas.width = img.naturalWidth || 200;
                          canvas.height = img.naturalHeight || 50;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0);
                          resolve(canvas.toDataURL('image/png'));
                        } catch (error) {
                          resolve(dataUrl);
                        }
                      };
                      img.onerror = () => resolve(dataUrl);
                      img.src = dataUrl;
                    } else {
                      resolve(dataUrl);
                    }
                  };
                  reader.onerror = () => resolve(null);
                  reader.readAsDataURL(blob);
                });
              } else {
                console.warn('Could not extract storage path from Firebase Storage URL');
                return null;
              }
            } catch (error) {
              console.warn('Error loading logo from Firebase Storage SDK:', error);
              // For Firebase Storage URLs, don't fall back to fetch (will cause CORS error)
              return null;
            }
          }
          
          // For non-Firebase Storage URLs (like local files), use fetch
          if (url.startsWith('http') || url.startsWith('/')) {
            try {
              const response = await fetch(url);
              if (!response.ok) throw new Error('Failed to fetch');
              const blob = await response.blob();
              
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const dataUrl = reader.result;
                  // If it's SVG, convert to PNG via canvas
                  if (url.endsWith('.svg') || blob.type === 'image/svg+xml') {
                    const img = new Image();
                    img.onload = () => {
                      try {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth || 200;
                        canvas.height = img.naturalHeight || 50;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                      } catch (error) {
                        resolve(dataUrl);
                      }
                    };
                    img.onerror = () => resolve(dataUrl);
                    img.src = dataUrl;
                  } else {
                    resolve(dataUrl);
                  }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.warn('Error fetching logo via fetch:', error);
              return null;
            }
          }
          
          // If all else fails, return null
          return null;
        } catch (error) {
          console.warn('Error loading logo:', error);
          return null;
        }
      };
      
      // Load logo as data URL (will be added directly to PDF pages, not in HTML)
      // Try company logo first, then fall back to BBA logo
      let logoDataUrl = null;
      let logoDimensions = { width: 200, height: 50 };
      
      console.log('Starting logo load, companyLogo:', companyLogo);
      
      // Try company logo first
      if (companyLogo) {
        console.log('Attempting to load company logo:', companyLogo);
        const logoLoadPromise = loadLogoAsDataUrl(companyLogo).catch((err) => {
          console.warn('Company logo load failed:', err);
          return null;
        });
        // Use longer timeout for Firebase Storage URLs (5 seconds)
        const isFirebaseStorageURL = companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
        const timeoutDuration = isFirebaseStorageURL ? 5000 : 2000; // 5 seconds for Firebase Storage, 2 seconds for others
        const timeoutPromise = new Promise((resolve) => setTimeout(() => {
          console.log('Company logo load timeout');
          resolve(null);
        }, timeoutDuration));
        logoDataUrl = await Promise.race([logoLoadPromise, timeoutPromise]);
        console.log('Company logo result:', logoDataUrl ? 'Success' : 'Failed');
      }
      
      // If company logo failed or doesn't exist, try BBA logo
      if (!logoDataUrl) {
        const bbaLogoUrl = '/bba-logo.png';
        console.log('Attempting to load BBA logo:', bbaLogoUrl);
        const logoLoadPromise = loadLogoAsDataUrl(bbaLogoUrl).catch((err) => {
          console.warn('BBA logo load failed:', err);
          return null;
        });
        const timeoutPromise = new Promise((resolve) => setTimeout(() => {
          console.log('BBA logo load timeout');
          resolve(null);
        }, 2000)); // 2 second timeout
        logoDataUrl = await Promise.race([logoLoadPromise, timeoutPromise]);
        console.log('BBA logo result:', logoDataUrl ? 'Success' : 'Failed');
      }
      
      console.log('Final logoDataUrl:', logoDataUrl ? 'Has logo' : 'No logo');
      
      // Get logo dimensions if we have a logo
      if (logoDataUrl) {
        console.log('Getting logo dimensions');
        const img = new Image();
        await Promise.race([
          new Promise((resolve) => {
            img.onload = () => {
              logoDimensions = { width: img.naturalWidth || img.width || 200, height: img.naturalHeight || img.height || 50 };
              console.log('Logo dimensions:', logoDimensions);
              resolve();
            };
            img.onerror = () => {
              console.warn('Error loading logo image for dimensions');
              resolve();
            };
            img.src = logoDataUrl;
          }),
          new Promise((resolve) => setTimeout(() => {
            console.log('Logo dimensions timeout');
            resolve();
          }, 2000)) // 2 second timeout
        ]);
      }
      
      // Helper function to safely escape HTML and handle text
      const escapeHtml = (text) => {
        if (text === null || text === undefined) return '';
        const textStr = String(text);
        return textStr
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };

      const subItemsPdfHtml = (subItems) => {
        const list = normalizeSopList(subItems);
        if (!list.length) return '';
        return `
          <ul style="margin: 8px 0 0 0; padding-left: 20px; list-style-type: circle;">
            ${list.map((subItem) => {
              const t = getSopNodeText(subItem);
              const nested = typeof subItem === 'string' ? [] : normalizeSopList(subItem.subItems);
              return `
                <li style="margin-bottom: 4px; color: black; font-size: 13px; word-wrap: break-word;">
                  ${escapeHtml(t || 'Empty sub-item')}
                  ${nested.length ? subItemsPdfHtml(nested) : ''}
                </li>
              `;
            }).join('')}
          </ul>
        `;
      };
      
      // Create the PDF content container
      const sop = selectedSOP;
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.color = 'black';
      tempContainer.style.padding = '40px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.wordWrap = 'break-word';
      tempContainer.style.overflowWrap = 'break-word';
      
      // Prefer the Tiptap JSON so the PDF matches the on-screen SOP exactly;
      // fall back to the legacy structured rendering for records without it.
      const richJson = sop.contentJSON;
      const hasRich = richJson && !isEmptyDoc(richJson);
      const richHtml = hasRich ? sopJsonToHtml(richJson) : '';

      // Master HTML wrapper (CRM ▸ SOP Templates ▸ HTML Template tab). When one
      // is saved it replaces the built-in layout below — tokens are swapped for
      // the client's branding plus this SOP's title/content.
      let masterHtml = '';
      try {
        masterHtml = (await fetchSopHtmlTemplate()).html;
      } catch (e) {
        console.error('Error fetching SOP HTML template:', e);
      }

      // Build HTML content (logo will be added directly to PDF pages using jsPDF to avoid CORS)
      const sopStyles = buildSopContentStyles(primaryColor);
      const bodyHtml = hasRich ? `<div class="sop-pdf">${richHtml}</div>` : `
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 22px; font-weight: bold; color: ${secondaryColor}; border-bottom: 2px solid ${primaryColor}; padding-bottom: 5px; margin-bottom: 15px;">Description</h2>
          <p style="margin: 0; word-wrap: break-word; line-height: 1.6; font-size: 14px; color: black;">${escapeHtml(sop.description || 'No description provided')}</p>
        </div>

        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 22px; font-weight: bold; color: ${secondaryColor}; border-bottom: 2px solid ${primaryColor}; padding-bottom: 5px; margin-bottom: 15px;">Steps</h2>
          ${(sop.steps || []).map((step, index) => `
            <div class="step-container" data-step-index="${index}" style="margin-bottom: 0; padding: 15px; padding-top: ${index === 0 ? '15px' : '5px'}; page-break-inside: avoid; break-inside: avoid; orphans: 3; widows: 3;">
              <h3 style="font-size: 18px; font-weight: bold; color: black; margin: 0 0 8px 0; word-wrap: break-word;">${index + 1}. ${escapeHtml(step.title || `Step ${index + 1}`)}</h3>
              ${step.description && step.description.trim() !== '' ?
                `<p style="margin: 0 0 12px 0; color: black; word-wrap: break-word; line-height: 1.6; font-size: 14px;">${escapeHtml(step.description)}</p>` : ''
              }
              ${(step.descriptionItems || []).length > 0 ? `
                <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
                  ${(step.descriptionItems || []).map((item, itemIndex) => {
                    const itemText = typeof item === 'string' ? item : (item.text || '');
                    const subItems = typeof item === 'string' ? [] : normalizeSopList(item.subItems);
                    return `
                      <li style="margin-bottom: 8px; word-wrap: break-word; color: black; font-size: 14px;">
                        ${escapeHtml(itemText || 'Empty item')}
                        ${subItems.length > 0 ? subItemsPdfHtml(subItems) : ''}
                      </li>
                    `;
                  }).join('')}
                </ul>
              ` : ''}
            </div>
          `).join('')}
        </div>
      `;

      if (masterHtml.trim()) {
        tempContainer.innerHTML = sopStyles + sanitizeHtml(renderSopHtmlTemplate(masterHtml, {
          logoUrl: logoDataUrl || '',
          title: sop.title || '',
          body: bodyHtml,
          companyName,
          primaryColor,
          secondaryColor,
        }));
        // The logo now renders inside the HTML itself — skip the per-page
        // jsPDF header logo so it isn't duplicated.
        logoDataUrl = null;
      } else {
        tempContainer.innerHTML = `
          ${sopStyles}
          <div style="text-align: center; margin-bottom: 24px;">
            <h1 style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 10px; word-wrap: break-word;">${escapeHtml(sop.title)}</h1>
          </div>
          ${bodyHtml}
        `;
      }

      document.body.appendChild(tempContainer);
      
      // Wait for all images (including logo) to load
      const images = tempContainer.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
            setTimeout(resolve, 3000); // Timeout after 3 seconds
          });
        })
      );
      
      // Wait for DOM to render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Convert to canvas with better page break handling
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false,
        removeContainer: false,
        onclone: (clonedDoc) => {
          // Ensure page-break-inside: avoid is applied and add additional break controls
          const clonedContainer = clonedDoc.querySelector('div');
          if (clonedContainer) {
            const stepDivs = clonedContainer.querySelectorAll('.step-container');
            stepDivs.forEach(div => {
              div.style.pageBreakInside = 'avoid';
              div.style.breakInside = 'avoid';
              div.style.orphans = '3';
              div.style.widows = '3';
              // Also prevent breaking the step title
              const stepTitle = div.querySelector('h3');
              if (stepTitle) {
                stepTitle.style.pageBreakAfter = 'avoid';
                stepTitle.style.breakAfter = 'avoid';
              }
            });
          }
        }
      });
      
      // Remove temporary container
      document.body.removeChild(tempContainer);
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const pageMargin = 10; // 10mm margin
      const logoHeight = logoDataUrl ? 20 : 0; // Logo height in mm (reduced from 30 to fit better)
      const logoBottomMargin = logoDataUrl ? 5 : 0; // Space below logo
      const headerHeight = logoDataUrl ? logoHeight + logoBottomMargin : 0; // Total space for logo
      const footerHeight = 15; // Space for footer (company name + page number)
      const contentAreaHeight = pdfHeight - pageMargin - headerHeight - pageMargin - footerHeight;
      
      // Calculate image dimensions
      const imgWidth = pdfWidth - (pageMargin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Function to add header (logo) and footer (company name + page number) to each page
      const addPageHeaderFooter = (pageNum, totalPages) => {
        // Add logo centered at top on every page
        if (logoDataUrl) {
          try {
            console.log(`Adding logo to page ${pageNum}, logoDataUrl exists:`, !!logoDataUrl);
            const maxLogoHeight = 20; // 20mm max height (reduced to fit better in header)
            const maxLogoWidth = 70; // 70mm max width (reduced proportionally)
            const logoAspectRatio = logoDimensions.width / logoDimensions.height;
            
            let calculatedLogoHeight = maxLogoHeight;
            let calculatedLogoWidth = calculatedLogoHeight * logoAspectRatio;
            
            // If width exceeds max, scale down
            if (calculatedLogoWidth > maxLogoWidth) {
              calculatedLogoWidth = maxLogoWidth;
              calculatedLogoHeight = calculatedLogoWidth / logoAspectRatio;
            }
            
            // Center the logo horizontally
            const logoX = (pdfWidth - calculatedLogoWidth) / 2;
            
            console.log(`Adding logo with dimensions: ${calculatedLogoWidth}mm x ${calculatedLogoHeight}mm`);
            pdf.addImage(logoDataUrl, 'PNG', logoX, pageMargin, calculatedLogoWidth, calculatedLogoHeight);
            console.log('Logo added successfully to page', pageNum);
          } catch (error) {
            console.error('Error adding logo to PDF:', error);
          }
        } else {
          console.log(`No logo to add to page ${pageNum}`);
        }
        
        // Add company name at bottom left
        if (companyName) {
          pdf.setFontSize(10);
          pdf.setTextColor(100, 100, 100);
          pdf.text(companyName, pageMargin, pdfHeight - pageMargin);
        }
        
        // Add date in the middle of footer
        const currentDate = new Date().toLocaleDateString();
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const dateWidth = pdf.getTextWidth(currentDate);
        pdf.text(currentDate, (pdfWidth - dateWidth) / 2, pdfHeight - pageMargin);
        
        // Add page number at bottom right
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${pageNum} of ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdfWidth - pageMargin - textWidth, pdfHeight - pageMargin);
      };
      
      // Split content across pages properly - use conservative approach to never split text
      // We'll use a larger buffer and be very careful about line boundaries
      const baseContentAreaHeight = contentAreaHeight - 30; // 30mm buffer to be safe
      
      // Estimate line height more accurately
      // Font size is 14px, line height is 1.6, so approximately 22-24px per line
      // But we'll be more conservative and use a larger value
      const estimatedLineHeight = 28; // pixels - slightly larger to be safe
      const estimatedLineHeightInMM = (estimatedLineHeight / canvas.height) * imgHeight; // Convert to mm
      
      // Function to round down to nearest line boundary (for start positions)
      const roundDownToLineBoundary = (position) => {
        if (position <= 0) return 0;
        const positionInPixels = (position / imgHeight) * canvas.height;
        const linesFromTop = Math.floor(positionInPixels / estimatedLineHeight);
        const roundedPixels = linesFromTop * estimatedLineHeight;
        return (roundedPixels / canvas.height) * imgHeight;
      };
      
      // Function to round up to nearest line boundary (for end positions)
      const roundUpToLineBoundary = (position) => {
        if (position >= imgHeight) return imgHeight;
        const positionInPixels = (position / imgHeight) * canvas.height;
        const linesFromTop = Math.ceil(positionInPixels / estimatedLineHeight);
        const roundedPixels = linesFromTop * estimatedLineHeight;
        return Math.min((roundedPixels / canvas.height) * imgHeight, imgHeight);
      };
      
      // Track where each page ends to prevent overlaps
      let currentY = 0;
      const pages = [];
      
      // Pre-calculate all page boundaries to ensure no text splitting
      while (currentY < imgHeight) {
        const remainingHeight = imgHeight - currentY;
        
        if (remainingHeight <= baseContentAreaHeight) {
          // Last page - take all remaining content
          pages.push({
            startY: currentY,
            endY: imgHeight,
            height: imgHeight - currentY
          });
          break;
        }
        
        // Calculate ideal end position
        const idealEndY = currentY + baseContentAreaHeight;
        
        // Round down the end position to ensure we don't cut text
        // We want to end at a complete line, so round down, then add one full line
        const roundedDownEnd = roundDownToLineBoundary(idealEndY);
        const safeEndY = Math.min(roundedDownEnd + estimatedLineHeightInMM, imgHeight);
        
        // Ensure we have at least some content on this page
        const pageEndY = Math.max(safeEndY, currentY + estimatedLineHeightInMM * 3); // At least 3 lines
        const finalEndY = Math.min(pageEndY, imgHeight);
        
        pages.push({
          startY: currentY,
          endY: finalEndY,
          height: finalEndY - currentY
        });
        
        // Next page starts exactly where this one ends
        currentY = finalEndY;
      }
      
      const totalPages = pages.length;
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) {
          pdf.addPage();
        }
        
        // Add header and footer first
        addPageHeaderFooter(pageNum, totalPages);
        
        const page = pages[pageNum - 1];
        const sourceY = page.startY;
        const pageContentHeight = page.height;
        
        const destY = pageMargin + headerHeight; // Start content below the logo
        
        // Calculate the source rectangle in the original canvas
        const sourceYInCanvas = (sourceY / imgHeight) * canvas.height;
        const sourceHeightInCanvas = (pageContentHeight / imgHeight) * canvas.height;
        
        // Create a temporary canvas for this page's slice
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeightInCanvas;
        const pageCtx = pageCanvas.getContext('2d');
        
        // Draw the slice of the original canvas
        pageCtx.drawImage(
          canvas,
          0, sourceYInCanvas, // Source x, y
          canvas.width, sourceHeightInCanvas, // Source width, height
          0, 0, // Destination x, y
          canvas.width, sourceHeightInCanvas // Destination width, height
        );
        
        // Convert to image data
        const pageImgData = pageCanvas.toDataURL('image/png');
        
        // Add to PDF
        pdf.addImage(pageImgData, 'PNG', pageMargin, destY, imgWidth, pageContentHeight);
      }
      
      // Download the PDF
      const fileName = `${String(sop.title || 'sop').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_sop.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleNewTemplate = () => {
    const newTemplate = {
      id: Date.now(),
      title: 'New SOP',
      description: 'SOP description',
      department: 'design',
      steps: []
    };
    setSelectedSOP(newTemplate);
    setEditedSOP(newTemplate);
    setEditedContentJSON(null);
    setIsNewTemplate(true);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseNewTemplate = () => {
    setIsModalOpen(false);
    setIsNewTemplate(false);
    setIsEditing(false);
    setSelectedSOP(null);
    setEditedSOP(null);
  };

  const handleDeleteNewTemplate = () => {
    setIsModalOpen(false);
    setIsNewTemplate(false);
    setIsEditing(false);
    setSelectedSOP(null);
    setEditedSOP(null);
  };


  // Search + group filter narrow the sections, mirroring the Sales resources tab.
  const q = searchQuery.trim().toLowerCase();
  const sopMatchesSearch = (sop, sectionLabel) => {
    if (!q) return true;
    const custom = userCustomizations[sop.id];
    const title = (custom?.title || sop.title || '').toLowerCase();
    const description = (custom?.description || sop.description || '').toLowerCase();
    return title.includes(q) || description.includes(q) || sectionLabel.toLowerCase().includes(q);
  };
  const visibleSections = SOP_SECTIONS
    .filter((s) => groupFilter === 'all' || s.id === groupFilter)
    .map((s) => ({ ...s, items: (sops[s.id] || []).filter((sop) => sopMatchesSearch(sop, s.label)) }))
    .filter((s) => s.items.length > 0);
  const showContactToBuild =
    (groupFilter === 'all' || groupFilter === CONTACT_TO_BUILD_SECTION.id) &&
    (!q || 'contact to build sop'.includes(q) || contactToBuildGoal.toLowerCase().includes(q));

  const renderSectionHeader = (section) => (
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
      <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
        <span className="text-xl leading-none">{groupEmojis[section.id] || section.emoji}</span>
      </span>
      <h2 className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {section.label}
      </h2>
      <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
    </div>
  );

  const cardShell = `rounded-xl border p-4 transition-shadow hover:shadow-md cursor-pointer ${
    darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
  }`;
  // Half-width variants for the View / Tailor button row (mirrors the Sales
  // resources cards): ghost View + red Tailor.
  const sopViewBtn = `flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
    darkMode ? 'border-gray-700 text-gray-200 hover:bg-gray-700/60' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
  }`;
  const sopTailorBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors';
  // Once tailored, the Tailor button flips green so the state reads from the
  // action itself rather than a separate pill.
  const sopTailoredBtn =
    'flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors';
  // Template version next to the title (mirrors the Sales resources cards).
  const versionPill = `shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
    darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
  }`;

  const renderSOPCard = (sop) => {
    const custom = userCustomizations[sop.id];
    const displayTitle = custom?.title || sop.title || 'Untitled SOP';
    const version = String(sop.version || '').trim();
    // Card subtitle always comes from the SOP template's own description — clients
    // tailor the document body, not this field — so it reflects the current template
    // rather than a stale snapshot saved in their customization doc.
    const displayDescription = sop.description || '';
    return (
      <div key={sop.id} className={cardShell} onClick={(event) => handleSOPClick(sop, event)}>
        <div className="flex items-center gap-3 mb-4">
          <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
            {sop.emoji ? (
              <span className="text-2xl leading-none">{sop.emoji}</span>
            ) : (
              <FileText className="w-5 h-5" />
            )}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {displayTitle}
              </h4>
              {version && <span className={versionPill}>v{version}</span>}
            </div>
            {displayDescription && (
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{displayDescription}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={(event) => handleSOPClick(sop, event)} className={sopViewBtn}>
            <Eye className="w-3.5 h-3.5" />
            View
          </button>
          <button type="button" onClick={(event) => handleSOPTailor(sop, event)} className={custom ? sopTailoredBtn : sopTailorBtn}>
            <Sparkles className="w-3.5 h-3.5" />
            {custom ? 'Tailored' : 'Tailor'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? '' : 'relative min-h-screen p-6'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? 'relative w-full' : 'relative max-w-7xl mx-auto'}>
        <div className={embedded ? 'rounded-lg' : 'rounded-lg p-6'}>
          {!embedded && (
            <div className="flex items-center justify-between mb-8">
              <BackButton onClick={() => navigate('/portal/operations')} />
              <div className="flex items-center">
                <Book className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Standard Operating Procedures</h1>
              </div>
              <div className="w-[120px] flex justify-end">
                <AddButton onClick={handleNewTemplate} />
              </div>
            </div>
          )}

          {/* Search + group filter (matches the Sales resources tab) */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  darkMode ? 'text-gray-500' : 'text-gray-400'
                }`}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search SOPs..."
                className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  darkMode
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className={`sm:w-56 px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white'
                  : 'bg-white border-gray-200 text-gray-900'
              }`}
            >
              <option value="all">All groups</option>
              {[...SOP_SECTIONS, CONTACT_TO_BUILD_SECTION].map((s) => (
                <option key={s.id} value={s.id}>
                  {(groupEmojis[s.id] || s.emoji)} {s.label}
                </option>
              ))}
            </select>
          </div>

          {visibleSections.length === 0 && !showContactToBuild && (
            <p className={`text-sm text-center py-10 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No SOPs match your search.
            </p>
          )}

          {visibleSections.map((section) => (
            <div key={section.id} className="mt-10 first:mt-0">
              {renderSectionHeader(section)}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((sop) => renderSOPCard(sop))}
              </div>
            </div>
          ))}

          {showContactToBuild && (
            <div className="mt-10 first:mt-0">
              {renderSectionHeader(CONTACT_TO_BUILD_SECTION)}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className={cardShell} onClick={() => setContactToBuildModalOpen(true)}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="shrink-0 w-11 h-11 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
                      <span className="text-2xl leading-none">{groupEmojis['contact-to-build'] || CONTACT_TO_BUILD_SECTION.emoji}</span>
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Contact to Build SOP
                        </h4>
                      </div>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {contactToBuildGoal}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setContactToBuildModalOpen(true);
                      }}
                      className={sopViewBtn}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleContactToBuildEdit();
                        setContactToBuildModalOpen(true);
                      }}
                      className={contactToBuildHasCustomization ? sopTailoredBtn : sopTailorBtn}
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {contactToBuildHasCustomization ? 'Tailored' : 'Tailor'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Flow-board view of the same steps — serpentine cards with arrows,
                  shown under the card so the process reads at a glance. */}
              <div className="mt-6">
                <ContactToBuildFlow
                  darkMode={darkMode}
                  steps={contactToBuildSteps.length ? contactToBuildSteps : contactToBuildTemplateSteps}
                  onStepClick={() => setContactToBuildModalOpen(true)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact to Build modal — same layout as the SOP modal below: X close
          top-left, centered title, scrollable body, pinned action footer. */}
      {contactToBuildModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative`}>
            <button
              type="button"
              onClick={() => {
                setContactToBuildEditing(false);
                setContactToBuildModalOpen(false);
              }}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 overflow-y-auto thin-scrollbar px-4 sm:px-6 md:px-8 pt-12 pb-4">
            <div className="w-full overflow-x-auto">
              <div className="flex flex-col items-center mb-6 sm:mb-8">
                <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Contact to Build SOP</h2>
                <p className={`text-base text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Goal: {contactToBuildGoal}
                </p>
              </div>
              <table className={`w-full border-collapse text-sm sm:text-base ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                <thead>
                  <tr className={`border-b-2 ${darkMode ? 'border-red-500 bg-gray-700' : 'border-red-500 bg-gray-100'}`}>
                    {contactToBuildEditing && (
                      <th className={`w-24 py-3 px-2 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} aria-label="Reorder / delete" />
                    )}
                    <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Action</th>
                    <th className={`text-left py-3 px-4 font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assigned to</th>
                  </tr>
                </thead>
                <tbody>
                  {(contactToBuildEditing ? contactToBuildEditedSteps : (contactToBuildSteps.length ? contactToBuildSteps : contactToBuildTemplateSteps)).map((row, index) => (
                    <tr
                      key={index}
                      className={`border-b ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'}`}
                    >
                      {contactToBuildEditing && (
                        <td className="py-2 px-2 align-middle">
                          <div className="flex items-center gap-0.5">
                            <button
                              type="button"
                              onClick={() => handleContactToBuildMoveRow(index, 'up')}
                              disabled={index === 0}
                              className={`p-1 rounded ${index === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                              title="Move up"
                              aria-label="Move row up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleContactToBuildMoveRow(index, 'down')}
                              disabled={index === contactToBuildEditedSteps.length - 1}
                              className={`p-1 rounded ${index === contactToBuildEditedSteps.length - 1 ? 'opacity-40 cursor-not-allowed' : ''} ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                              title="Move down"
                              aria-label="Move row down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleContactToBuildDeleteRow(index)}
                              className="p-1 rounded text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                              title="Delete row"
                              aria-label="Delete row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                      {contactToBuildEditing ? (
                        <>
                          <td className="py-2 px-4 align-top">
                            <input
                              type="text"
                              value={row.action}
                              onChange={(e) => handleContactToBuildStepChange(index, 'action', e.target.value)}
                              className={`w-full min-w-0 border rounded px-2 py-1.5 text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </td>
                          <td className="py-2 px-4 align-top">
                            <input
                              type="text"
                              value={row.assignedTo}
                              onChange={(e) => handleContactToBuildStepChange(index, 'assignedTo', e.target.value)}
                              className={`w-full min-w-0 border rounded px-2 py-1.5 text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4">{row.action}</td>
                          <td className="py-3 px-4">{row.assignedTo}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {contactToBuildEditing && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={handleContactToBuildAddRow}
                    className={`text-red-600 hover:text-red-700 flex items-center text-sm ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded px-2 py-1 -ml-2`}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add row
                  </button>
                </div>
              )}
              <p className={`mt-6 text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {contactToBuildNote}
              </p>
            </div>
            </div>

            {/* Action Buttons — always visible (mirrors the SOP modal footer) */}
            <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-4 sm:px-6 md:px-8 py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                {contactToBuildHasCustomization && (
                  <button
                    type="button"
                    onClick={handleContactToBuildResetToTemplate}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Reset to Template
                  </button>
                )}
                {!contactToBuildEditing && <EditButton label="Edit" onClick={handleContactToBuildEdit} />}
              </div>
              <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                {contactToBuildEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={handleContactToBuildCancel}
                      className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                    >
                      Cancel
                    </button>
                    <SaveButton onClick={handleContactToBuildSave} variant="green" />
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleDownloadContactToBuildDoc}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download DOC</span>
                      <span className="sm:hidden">DOC</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadContactToBuildPDF}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Download PDF</span>
                      <span className="sm:hidden">PDF</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal */}
      {isModalOpen && selectedSOP && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div ref={modalRef} className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col relative`}>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              title="Close"
              aria-label="Close"
              className={`absolute top-3 left-3 z-10 p-1.5 rounded-full transition ${darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <X className="w-5 h-5" />
            </button>
              {(() => {
                try {
                  return (
                    <>
                      <div className="flex-1 overflow-y-auto thin-scrollbar px-4 sm:px-6 md:px-8 pt-12 pb-4">
                      <div className="flex flex-col items-center mb-6 sm:mb-8">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedSOP.title}
                            onChange={(e) => setEditedSOP({ ...editedSOP, title: e.target.value })}
                            className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center border rounded px-2 py-1 w-full ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        ) : (
                          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedSOP.title}</h2>
                        )}
                      </div>

                      <div className="space-y-4 sm:space-y-6">
                {/* Attachment-only template: show attachment with View/Download */}
                {((isEditing ? editedSOP : selectedSOP)?.attachmentOnly && (isEditing ? editedSOP : selectedSOP)?.attachment) ? (
                  <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 sm:p-6`}>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Attachment</h3>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {(isEditing ? editedSOP : selectedSOP).attachment.name}
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleAttachmentView((isEditing ? editedSOP : selectedSOP).attachment)}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        View
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAttachmentDownload((isEditing ? editedSOP : selectedSOP).attachment)}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {isNewTemplate && isEditing && (
                      <div className={`rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-4 sm:p-6`}>
                        <h3 className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Department</h3>
                        <select
                          value={editedSOP.department || 'design'}
                          onChange={(e) => setEditedSOP({ ...editedSOP, department: e.target.value })}
                          className={`w-full border rounded px-2 py-1 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                        >
                          <option value="design">Design</option>
                          <option value="construction">Construction</option>
                          <option value="office">Office</option>
                        </select>
                      </div>
                    )}
                    {isEditing ? (
                      <SopRichEditor
                        mode={isInternal ? 'full' : 'client'}
                        value={editedContentJSON}
                        onChange={setEditedContentJSON}
                        darkMode={darkMode}
                        toolbarSlot={toolbarSlot}
                      />
                    ) : (
                      <SopRichView
                        value={selectedSOP.contentJSON || migrateSopRecord(selectedSOP)}
                        darkMode={darkMode}
                      />
                    )}
                  </>
                )}
              </div>
                      </div>

                             {/* Action Buttons — always visible */}
               <div className={`shrink-0 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} px-4 sm:px-6 md:px-8 py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
                 <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                   {isEditing && <div ref={setToolbarSlot} />}
                   {selectedSOP.hasCustomizations && !isNewTemplate && (
                     <button
                       onClick={handleResetToTemplate}
                       className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                     >
                       Reset to Template
                     </button>
                   )}
                   {!isEditing && !isNewTemplate && !(selectedSOP?.attachmentOnly && selectedSOP?.attachment) && (
                     <EditButton label="Edit" onClick={handleEdit} />
                   )}
                   {isNewTemplate && (
                     <button
                       onClick={handleDeleteNewTemplate}
                       className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                     >
                       <Trash2 className="w-4 h-4 mr-2" />
                       Delete
                     </button>
                   )}
                 </div>
                 <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                   {isNewTemplate ? (
                     <>
                       <button
                         onClick={handleCloseNewTemplate}
                         className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                       >
                         Close
                       </button>
                       <SaveButton onClick={handleSave} variant="green" />
                     </>
                   ) : (
                     <>
{isEditing ? (
                        <>
                          <button
                            onClick={handleCancel}
                            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                          >
                            Cancel
                          </button>
                          <SaveButton onClick={handleSave} variant="green" />
                        </>
                       ) : (
                         <>
                           {selectedSOP?.attachmentOnly && selectedSOP?.attachment ? (
                             <button
                               type="button"
                               onClick={() => handleAttachmentDownload(selectedSOP.attachment)}
                               className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                             >
                               <Download className="w-4 h-4 mr-2" />
                               <span className="hidden sm:inline">Download</span>
                               <span className="sm:hidden">Download</span>
                             </button>
                           ) : (
                             <>
                               <button
                                 onClick={handleDownloadDoc}
                                 className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                               >
                                 <Download className="w-4 h-4 mr-2" />
                                 <span className="hidden sm:inline">Download DOC</span>
                                 <span className="sm:hidden">DOC</span>
                               </button>
                               <button
                                 onClick={handleDownloadPDF}
                                 className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                               >
                                 <Download className="w-4 h-4 mr-2" />
                                 <span className="hidden sm:inline">Download PDF</span>
                                 <span className="sm:hidden">PDF</span>
                               </button>
                             </>
                           )}
                         </>
                       )}
                     </>
                   )}
                 </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error rendering SOP modal:', error);
                  return (
                    <div className="text-center py-8">
                      <h2 className="text-xl font-semibold text-red-600 mb-4">Error Loading SOP</h2>
                      <p className="text-gray-600 mb-4">There was an error loading this SOP. Please try again.</p>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Close
                      </button>
                    </div>
                  );
                }
              })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPs;